import { useState, useMemo, useEffect } from "react";
import styled from "@emotion/styled";
import {css} from '@emotion/react';
import { useFormData } from "../../../contexts/FormData";

import { useFormikContext } from "formik";

import { useQuery } from "@tanstack/react-query";

import { getShipmentItemsById } from "../apiService";

import { BasicFlex, ScrollableContainer, InputLabel } from "../../../utils/Common";

import SimpleChip from "../../../utils/utilities/SimpleChip";

import { IconButton, lighten, darken, Box, TextField, Checkbox, FormControlLabel, useTheme } from "@mui/material";

import {
  RadioButtonUnchecked as UnselectedIcon,
  CheckCircle as SelectedIcon,
  Note,
} from '@mui/icons-material';

import CloseRounded from '@mui/icons-material/CloseRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import AttachMoneyRoundedIcon from '@mui/icons-material/AttachMoneyRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import InfoOutlineRoundedIcon from '@mui/icons-material/InfoOutlineRounded';
import RemoveCircleOutlineRoundedIcon from '@mui/icons-material/RemoveCircleOutlineRounded';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';

import { formatCurrency } from "../../../utils/util";

import { startCase } from "lodash";

const TableContainer = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  width: 100%;
  margin-top: .3rem;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  tr {
    text-align: left;
  }
  tr .right {
    text-align: right;
  }
`

const THead = styled.thead`
  /* background-color: #f5f5f5; */
  background: ${props => props.theme.colors.softBg};

`

const HRow = styled.tr`
 /* &:hover {
    background: #f9fafb;
  } */
`

const HCell = styled.th`
  padding: 1.2rem 1.6rem;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
  /* background-color: #f9fafb; */
  font-weight: 600;
  color: #6b7280;

  /* input {
    height: 18px;
    width: 18px;
  } */
`
const BRow = styled.tr`
  cursor: pointer;
  background-color: ${props => props.$selected ? lighten(props.theme.colors.success, .9) : "inherit"};
  color: ${props => props.$selected ? darken(props.theme.colors.success, .6) : 'inherit'};

  &:last-child td {
    border-bottom: none;
  }

  &:hover {
    background: ${props => !props.$selected ? "#f9fafb" : lighten(props.theme.colors.success, .87)};
  }

  ${props => props.$disabled && css/* closed */`
    
    cursor: not-allowed;
    pointer-events: none;

    td {
      opacity: .5;
    }

    td.opaque {
      opacity: 1;
    }
  `}
`

const BCell = styled.td`
  padding: 1.6rem;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;

  &.check {
    width: 60px;
  }
`

const PriceDisplay = styled.div`
  height: min-content;
  display: flex;
  justify-content: flex-end;

  .MuiInputBase-input {
    padding: .2rem !important;
  }
`

const NoteDisplay = styled(BasicFlex)`
  background-color: ${props => lighten(props.theme.colors.info, .75)};
  padding: 1.6rem;
  border-radius: 5px;
  align-items: flex-start;
  gap: 1.5rem;
  margin-bottom: 3rem;
`

const NoteTitle = styled.div`
  font-weight: 600;
  color: ${props => darken(props.theme.colors.info, .3)};
  font-size: 1.7rem;
  margin-bottom: .5rem;
`

const NoteText = styled.div`
  color: ${props => darken(props.theme.colors.info, .3)};
  /* opacity: .9; */
  font-weight: 500;
  line-height: 2.2rem;
  letter-spacing: .5px;
  font-size: 1.5rem;
`

const StatusText = styled.span`
  color: ${props => props.$color} !important;
  font-weight: 500;
`

const StatusChip = ({ effective_status }) => {
  const theme = useTheme();

  let Icon, color;
  switch (effective_status) {
    case "accepted":
      Icon = CheckRoundedIcon;
      color = "success";
      break;
    case "stocked":
      Icon = Inventory2RoundedIcon;
      color = "success";
      break;
    default:
      Icon = HourglassTopRoundedIcon;
      color = "warning";
      break;
  }

  let textColor = theme.palette[color].main;

  return <BasicFlex width="auto" justify="flex-end" gap=".5rem"><Icon color={color} fontSize="small" sx={{fontSize: "1.6rem"}} /> <StatusText $color={textColor}>{startCase(effective_status)}</StatusText></BasicFlex>

}


const ItemList = () => {
  const { values } = useFormikContext() || {};

  const { formValues, setFormValues } = useFormData() || {};

  let items = formValues.items || []

  let selectedItems = useMemo(() => items.filter(el => el.checked), [items]);

  const { data: shipmentItems } = useQuery({
    queryKey: ["nt_shipment_items", values?.nt_shipment_id],
    queryFn: () => getShipmentItemsById(values?.nt_shipment_id),
    enabled: !!values?.nt_shipment_id
  })

  useEffect(() => {
    if (shipmentItems?.length) {
      setFormValues(prevState => {
        return {...prevState, items: [...shipmentItems.map(el => ({...el, checked: el.status === "accepted" ? true : false}))]}
      })
    }
  }, [shipmentItems])

  const handleChange = (e, index) => {
    // console.log(e.target.name, e.target.value);
    let val = e.target.value, name = e.target.name;
    setFormValues(prevState => {
      let newItems = prevState.items.map((item, i) => i === index ? {...item, [name]: val} : item)
      return {...prevState, items: newItems}
    })
  }

  const handleSelect = (e, index) => {
    let checked = e.target.checked;
    setFormValues(prevState => {
      if (index || index === 0) {
        let newItems = prevState.items.map((item, i) => i === index ? {...item, checked} : item)
        return {...prevState, items: newItems}
      } else {
        let newItems = prevState.items.map((item, i) => ({...item, checked: item.effective_status === "stocked" ? item.checked : checked}))
        return {...prevState, items: newItems}
      }
    })
  }

  return ( 
    <BasicFlex dir="column">
      <NoteDisplay>
        <InfoOutlineRoundedIcon color="info" />
        <BasicFlex dir="column">
         <NoteTitle>Only accept items that have arrived</NoteTitle>

          <NoteText>This consignment was created by the vendor at the time of shipping. <br /> Mark a wig as accepted only after it physically arrives and you've inspected it.</NoteText>
        </BasicFlex>
      </NoteDisplay>
      <BasicFlex px=".3rem"><InputLabel>Shipment has {items?.length} consigned wigs</InputLabel> <InputLabel>{selectedItems?.length} accepted</InputLabel>
      </BasicFlex>

      <TableContainer>
        <Table>
          <THead>
            <HRow>
              <HCell className="check">
                {/* <input type="checkbox" onChange={(e) => handleSelect(e)} /> */}
                  <Checkbox onChange={(e) => handleSelect(e)} sx={{padding: 0}} color="success" size="small" />
                </HCell>
              <HCell>Wig ID</HCell>
              <HCell>SKU</HCell>
              <HCell className="right">Status</HCell>
              <HCell className="right">Cost</HCell>
              <HCell className="right">Price</HCell>
            </HRow>
          </THead>

          <tbody>
            {items.map((item, index) => {
              let stocked = item.effective_status === "stocked";
              return (
              <BRow key={index} onClick={(e) => handleSelect({target: {checked: !item.checked}}, index)} $selected={item.checked} $disabled={item.effective_status === "stocked"}>
                <BCell className="check">
                  {/* <input type="checkbox" checked={item.checked} /> */}
                  {item.checked ? <TaskAltRoundedIcon color="success" fontSize="small" /> : stocked ? <RemoveCircleOutlineRoundedIcon /> : <RadioButtonUncheckedRoundedIcon fontSize="small" />}
                </BCell>
                <BCell>{item.wig_id}</BCell>
                <BCell>{item.sku}</BCell>
                <BCell className="right opaque"><StatusChip effective_status={item.effective_status} /></BCell>
                <BCell className="right">
                  <PriceDisplay>
                    <TextField name="cost" type="number" value={item.cost} size="small" variant="outlined" onChange={(e) => handleChange(e, index)} InputProps={{sx: {margin: 0, width: "10rem", paddingLeft: ".5rem !important", gap: ".5rem", backgroundColor: "#fff"}, startAdornment: <AttachMoneyRoundedIcon fontSize='small' />}} />
                  </PriceDisplay>
                </BCell>
                <BCell className="right">
                  <PriceDisplay>
                    <TextField name="price" type="number" value={item.price} size="small" variant="outlined" onChange={(e) => handleChange(e, index)} InputProps={{sx: {margin: 0, width: "10rem", paddingLeft: ".5rem !important", gap: ".5rem", backgroundColor: "#fff"}, startAdornment: <AttachMoneyRoundedIcon fontSize='small' />}} />
                  </PriceDisplay>
                  </BCell>
              </BRow>
            )
            })}

          </tbody>
        </Table>
      </TableContainer>
      
      
    </BasicFlex>
  ) 
};

export default ItemList;